import { Component, OnInit } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { mergeMap, catchError, map } from 'rxjs/operators';
import { BaseComponent } from '../base/base.component';
import { QuoteInfo, QuotePresentationInfo, QuotePresentationSectionInfo, QuoteTemplateInfo } from 'src/app/models/models';
import {
	Quote_Presentation, Quote_Presentation_Image, Quote_Presentation_Section,
	Quote_Presentation_Row, Quote_Presentation_Item_Link, Quote_Item,
	Quote_Template, Quote_Template_Section, Quote_Template_Row
} from 'src/app/models/RestModels';

@Component({
	selector: 'app-custom-quote-designer',
	templateUrl: './custom-quote-designer.component.html',
	styleUrls: ['./custom-quote-designer.component.css']
})
export class CustomQuoteDesignerComponent extends BaseComponent implements OnInit {
	quote_info: QuoteInfo | null = null;
	presentation_info: QuotePresentationInfo | null = null;
	templates: Quote_Template[] = [];

	new_section_name: string = '';
	selected_section: QuotePresentationSectionInfo | null = null;
	is_new_presentation: boolean = false;

	ngOnInit(): void {
		this.loadTemplates();
		this.sink = this.route.paramMap.pipe(
			mergeMap((params) => {
				const id = Number(params.get('id'));
				return forkJoin({
					quote_info: this.rest.quote_info.get(id),
					presentation_info: this.rest.getQuotePresentationInfo(id)
				});
			})
		).subscribe((responses) => {
			this.quote_info = responses.quote_info;
			if (responses.presentation_info) {
				this.presentation_info = responses.presentation_info;
				// Sort bigger first (descending)
				this.presentation_info.sections.sort((a, b) => {
					const orderA = Number((a.section as any).sort_order) || 0;
					const orderB = Number((b.section as any).sort_order) || 0;
					return orderB - orderA;
				});
			} else {
				this.initNewPresentation();
			}
		}, (error) => this.showError(error));
	}

	loadTemplates() {
		this.rest.quote_template.search({ limit: 999, sort_order: ['name_ASC'] }).subscribe((responses) => {
			this.templates = responses.data;
		});
	}

	initNewPresentation() {
		this.is_new_presentation = true;
		this.presentation_info = {
			presentation: {
				quote_id: this.quote_info.quote.id,
				created: new Date(),
				updated: new Date(),
				project_name: this.quote_info.quote.name || 'Sin Nombre',
				symbology: 'COT-' + this.quote_info.quote.id
			},
			images: [],
			sections: []
		};
	}

	savePresentation() {
		this.is_loading = true;
		const obs = this.is_new_presentation
			? this.rest.quote_presentation.create(this.presentation_info.presentation)
			: this.rest.quote_presentation.update(this.presentation_info.presentation);

		obs.subscribe((response) => {
			this.presentation_info.presentation = response;
			this.is_new_presentation = false;
			this.is_loading = false;
			this.showSuccess('Presentación guardada');
		}, (error) => {
			this.is_loading = false;
			this.showError(error);
		});
	}

	addSection() {
		if (!this.new_section_name) return;

		const section: Quote_Presentation_Section = {
			id:0,
			quote_id: this.quote_info.quote.id,
			name: this.new_section_name || 'Nueva Sección',
			created: new Date(),
			price_display_mode: 'SUM',
			sort_order: this.presentation_info.sections.length
		};

		this.is_loading = true;
		this.rest.quote_presentation_section.create(section).subscribe((response) => {
			this.presentation_info.sections.push({
				section: response,
				rows: [],
				links: [],
				images: [],
				items: []
			});
			this.new_section_name = '';
			this.is_loading = false;
		}, (error) => {
			this.is_loading = false;
			this.showError(error);
		});
	}

	deleteSection(sectionInfo: QuotePresentationSectionInfo, index: number) {
		if (confirm('¿Estás seguro de eliminar esta sección y todos sus renglones?')) {
			this.is_loading = true;
			this.rest.quote_presentation_section.delete(sectionInfo.section).subscribe(() => {
				this.presentation_info.sections.splice(index, 1);
				this.is_loading = false;
			}, (error) => {
				this.is_loading = false;
				this.showError(error);
			});
		}
	}

	updateSection(section: Quote_Presentation_Section) {
		this.rest.quote_presentation_section.update(section).subscribe(() => {
			// Updated
		}, (error) => this.showError(error));
	}

	moveSectionUp(index: number) {
		if (index === 0) return;
		const temp = this.presentation_info.sections[index];
		this.presentation_info.sections[index] = this.presentation_info.sections[index - 1];
		this.presentation_info.sections[index - 1] = temp;
		this.recalculateSortOrders();
	}

	moveSectionDown(index: number) {
		if (index === this.presentation_info.sections.length - 1) return;
		const temp = this.presentation_info.sections[index];
		this.presentation_info.sections[index] = this.presentation_info.sections[index + 1];
		this.presentation_info.sections[index + 1] = temp;
		this.recalculateSortOrders();
	}

	recalculateSortOrders() {
		const total = this.presentation_info.sections.length;
		const observables = [];
		this.presentation_info.sections.forEach((sInfo, index) => {
			const newSortOrder = (total - index) * 10;
			const section: any = sInfo.section;
			if (section.sort_order !== newSortOrder) {
				section.sort_order = newSortOrder;
				observables.push(this.rest.quote_presentation_section.update(section));
			}
		});

		if (observables.length > 0) {
			this.is_loading = true;
			forkJoin(observables).subscribe(() => {
				this.is_loading = false;
			}, (error) => {
				this.is_loading = false;
				this.showError(error);
			});
		}
	}

	addRow(sectionInfo: QuotePresentationSectionInfo) {
		const row: Quote_Presentation_Row = {
			id:0,
			quote_presentation_section_id: sectionInfo.section.id,
			label: 'Nuevo Renglón',
			created: new Date(),
			description: '...',
			sort_order: sectionInfo.rows.length
		};

		this.rest.quote_presentation_row.create(row).subscribe((response) => {
			sectionInfo.rows.push(response);
		}, (error) => this.showError(error));
	}

	deleteRow(sectionInfo: QuotePresentationSectionInfo, row: Quote_Presentation_Row, index: number) {
		this.rest.quote_presentation_row.delete(row).subscribe(() => {
			sectionInfo.rows.splice(index, 1);
		}, (error) => this.showError(error));
	}

	updateRow(row: Quote_Presentation_Row) {
		this.rest.quote_presentation_row.update(row).subscribe(() => {
			// Updated
		}, (error) => this.showError(error));
	}

	linkItem(sectionInfo: QuotePresentationSectionInfo, quoteItem: Quote_Item) {
		const link: Quote_Presentation_Item_Link = {
			id: 0,
			quote_presentation_section_id: sectionInfo.section.id,
			created: new Date(),
			quote_item_id: quoteItem.id
		};

		this.rest.quote_presentation_item_link.create(link).subscribe((response) => {
			sectionInfo.links.push(response);
			if (!sectionInfo.items) sectionInfo.items = [];
			sectionInfo.items.push(quoteItem);
		}, (error) => this.showError(error));
	}

	unlinkItem(sectionInfo: QuotePresentationSectionInfo, link: Quote_Presentation_Item_Link, index: number) {
		this.rest.quote_presentation_item_link.delete(link).subscribe(() => {
			sectionInfo.links.splice(index, 1);
			sectionInfo.items.splice(index, 1);
		}, (error) => this.showError(error));
	}

	isItemLinked(quoteItemId: number): boolean {
		return this.presentation_info.sections.some(s => s.links.some(l => l.quote_item_id === quoteItemId));
	}

	getSectionTotal(sectionInfo: QuotePresentationSectionInfo): number {
		return sectionInfo.links.reduce((sum, link) => {
			const item = this.quote_info.items.find(i => i.quote_item.id === link.quote_item_id);
			return sum + (item ? item.quote_item.total : 0);
		}, 0);
	}

	uploadImage(event: any, type: 'PRIMARY_VISUAL' | 'SUPPORTING_DETAIL' | 'REFERENCE') {
		const file = event.target.files[0];
		if (!file) return;

		this.is_loading = true;

		this.sink = this.rest.uploadImage(file).pipe
		(
			mergeMap((image) => {
				const qpi: Quote_Presentation_Image = {
					id:0,
					quote_id: this.quote_info.quote.id,
					created: new Date(),
					image_id: image.id,
					type: type,
					sort_order: this.presentation_info.images.length
				};

				return this.rest.quote_presentation_image.create(qpi);
			})
		).subscribe((response) => {
			this.presentation_info.images.push(response);
			this.is_loading = false;
		}, (error) => {
			this.is_loading = false;
			this.showError(error);
		});
	}

	deleteImage(image: Quote_Presentation_Image, index: number) {
		this.rest.quote_presentation_image.delete(image).subscribe(() => {
			this.presentation_info.images.splice(index, 1);
		}, (error) => this.showError(error));
	}

	assignImageToSection(image: Quote_Presentation_Image, sectionInfo: QuotePresentationSectionInfo, index: number) {
		const update: Quote_Presentation_Image = {
			...image,
			quote_presentation_section_id: sectionInfo.section.id
		};

		this.rest.quote_presentation_image.update(update).subscribe((response) => {
			this.presentation_info.images.splice(index, 1);
			sectionInfo.images.push(response);
		}, (error) => this.showError(error));
	}

	unassignImageFromSection(image: Quote_Presentation_Image, sectionInfo: QuotePresentationSectionInfo, index: number) {
		const update: Quote_Presentation_Image = {
			...image,
			quote_presentation_section_id: null
		};

		this.rest.quote_presentation_image.update(update).subscribe((response) => {
			sectionInfo.images.splice(index, 1);
			this.presentation_info.images.push(response);
		}, (error) => this.showError(error));
	}

	saveAsTemplate() {
		const name = prompt('Nombre de la plantilla:');
		if (!name) return;

		this.is_loading = true;
		const template: Quote_Template = {
			id:0,
			name: name,
			created: new Date(),
			updated: new Date(),
			store_id: this.quote_info.quote.store_id
		};

		this.subs.sink = this.rest.quote_template.create( template ).subscribe((newTemplate) =>
		{
			const sectionObservables = this.presentation_info.sections.map((sInfo, sIdx) => {
				const ts: Quote_Template_Section = {
					id: 0,
					quote_template_id: newTemplate.id,
					created: new Date(),
					name: sInfo.section.name,
					price_display_mode: sInfo.section.price_display_mode,
					sort_order: sIdx
				};

				return this.rest.quote_template_section.create(ts).pipe(
					mergeMap((newTS) => {
						const rowObservables = sInfo.rows.map((row, rIdx) => {
							const tr: Quote_Template_Row = {
								id:0,
								quote_template_section_id: newTS.id,
								created: new Date(),
								label: row.label,
								description: row.description,
								sort_order: rIdx
							};

							return this.rest.quote_template_row.create(tr);
						});
						return rowObservables.length > 0 ? forkJoin(rowObservables) : of([]);
					})
				);
			});

			if (sectionObservables.length > 0) {
				forkJoin(sectionObservables).subscribe(() => {
					this.is_loading = false;
					this.showSuccess('Plantilla guardada correctamente');
					this.loadTemplates();
				}, (error) => {
					this.is_loading = false;
					this.showError(error);
				});
			} else {
				this.is_loading = false;
				this.showSuccess('Plantilla guardada (sin secciones)');
				this.loadTemplates();
			}
		}, (error) => {
			this.is_loading = false;
			this.showError(error);
		});
	}

	applyTemplate(templateId: string | number) {
		if (this.presentation_info.sections.length > 0) {
			if (!confirm('Esta acción agregará las secciones de la plantilla a las actuales. ¿Deseas continuar?')) {
				return;
			}
		}

		this.is_loading = true;
		this.rest.getQuoteTemplateInfo(Number(templateId)).subscribe((tInfo) => {
			if (!tInfo) {
				this.is_loading = false;
				return;
			}

			const sectionObservables = tInfo.sections.map((tsInfo) => {
				const section: Quote_Presentation_Section = {
					id: 0,
					quote_id: this.quote_info.quote.id,
					created: new Date(),
					name: tsInfo.section.name,
					price_display_mode: tsInfo.section.price_display_mode,
					sort_order: this.presentation_info.sections.length + tsInfo.section.sort_order
				};

				return this.rest.quote_presentation_section.create(section).pipe(
					mergeMap((newSection) => {
						const rowObservables = tsInfo.rows.map((tr) => {
							const row: Quote_Presentation_Row = {
								id: 0,
								quote_presentation_section_id: newSection.id,
								label: tr.label,
								created: new Date(),
								description: tr.description,
								sort_order: tr.sort_order
							};
							return this.rest.quote_presentation_row.create(row);
						});

						return forkJoin({
							rows: rowObservables.length > 0 ? forkJoin(rowObservables) : of([]),
							section: of(newSection)
						});
					})
				);
			});

			if (sectionObservables.length > 0) {
				forkJoin(sectionObservables).subscribe((results) => {
					results.forEach((res) => {
						this.presentation_info.sections.push({
							section: (res as any).section,
							rows: (res as any).rows as Quote_Presentation_Row[],
							links: [],
							images: [],
							items: []
						});
					});
					this.is_loading = false;
					this.showSuccess('Plantilla aplicada correctamente');
				}, (error) => {
					this.is_loading = false;
					this.showError(error);
				});
			} else {
				this.is_loading = false;
			}
		}, (error) => {
			this.is_loading = false;
			this.showError(error);
		});
	}
}
